import { useCallback, useEffect, useRef } from 'react';
import { gql } from '@apollo/client';
import { useApolloClient } from '@apollo/client/react';
import { useLocation } from 'react-router-dom';
import { useHostAuth } from './auth-context';
import { useDocumentVisible } from './document-visible';

/**
 * Telling the API this person is still here, once a minute — and which screen
 * they are on, whenever that changes.
 *
 * Shared by both products rather than owned by one. The API keys presence on
 * the person *and* the product, so somebody with iQD Pro and the portal open
 * at once is two entries and two session histories — which is the truthful
 * answer, and the reason the app is a parameter here rather than a constant.
 *
 * It is a heartbeat rather than anything derived from the WebSocket because
 * `graphql-ws` connects lazily: the socket opens when a screen starts its
 * first subscription, and most screens never start one, so a person reading a
 * report all afternoon would show as offline the entire time.
 *
 * Nothing here is allowed to matter. A failed heartbeat is swallowed — the
 * roster's own expiry is the backstop, and a presence feature that can put an
 * error in front of somebody trying to do their job has its priorities the
 * wrong way round.
 */

export const RECORD_PRESENCE = gql`
  mutation RecordPresence($app: String!, $route: String) {
    recordPresence(app: $app, route: $route)
  }
`;

export const END_PRESENCE = gql`
  mutation EndPresence($app: String!) {
    endPresence(app: $app)
  }
`;

/** The app keys the two products report under. */
export type PresenceApp = 'iqd-pro' | 'distributor-portal';

/** Matches the API's expectation. Two missed reports and the entry lapses. */
export const HEARTBEAT_MS = 60_000;

/**
 * The shortest gap between two reports caused by moving around.
 *
 * Moving to another screen is worth reporting on its own — the roster's screen
 * column is otherwise only as fresh as the last person to arrive or leave, and
 * on a quiet afternoon that is an hour ago. But somebody clicking through a
 * product is not somebody the admin screen needs told about four times a
 * second, and each report the API decides is news is a roster published to
 * everybody watching. So a navigation reports at once if the last report is
 * already this old, and otherwise waits until it is.
 */
export const ROUTE_REPORT_MIN_MS = 15_000;

export function usePresenceHeartbeat(app: PresenceApp): void {
  const client = useApolloClient();
  const { isAuthenticated } = useHostAuth();
  const visible = useDocumentVisible();
  const { pathname } = useLocation();

  /**
   * The screen to report, read at the moment of reporting.
   *
   * Held in a ref rather than in the effect's dependencies: moving between
   * screens would otherwise tear down the interval and start a new one, so the
   * minute would restart from every navigation. The screen is reported by the
   * throttled effect below instead, which leaves the interval alone.
   */
  const routeRef = useRef(pathname);
  useEffect(() => {
    routeRef.current = pathname;
  }, [pathname]);

  /** The screen the API has been told about, and when it was last told anything. */
  const reportedRoute = useRef<string | null>(null);
  const lastReportAt = useRef(0);

  const beat = useCallback(() => {
    lastReportAt.current = Date.now();
    reportedRoute.current = routeRef.current;
    client
      .mutate({
        mutation: RECORD_PRESENCE,
        variables: { app, route: routeRef.current },
        // Nothing reads the answer, and writing a scalar under ROOT_MUTATION
        // for it every minute is litter.
        fetchPolicy: 'no-cache',
      })
      .catch(() => undefined);
  }, [app, client]);

  useEffect(() => {
    // A tab behind five others is not somebody at their desk, and a laptop
    // asleep with the tab open is certainly not. Going quiet is exactly how
    // the API is told; it lapses on its own after two and a half minutes.
    if (!isAuthenticated || !visible) {
      return;
    }

    // Immediately, then on the interval: coming back to a tab that has been
    // behind another all lunchtime should put the person back on the roster
    // now rather than in fifty-nine seconds.
    beat();
    const timer = setInterval(beat, HEARTBEAT_MS);
    return () => clearInterval(timer);
  }, [beat, isAuthenticated, visible]);

  useEffect(() => {
    // Nothing to say when the API already has this screen — which is the case
    // on the way in, and every time the effect above has just beaten for some
    // other reason.
    if (!isAuthenticated || !visible || reportedRoute.current === pathname) {
      return;
    }

    // Cleared and rescheduled by each further navigation, off a deadline that
    // does not move: a burst of clicking sends one report carrying wherever it
    // ended up, rather than one per screen passed through.
    const timer = setTimeout(
      beat,
      Math.max(0, ROUTE_REPORT_MIN_MS - (Date.now() - lastReportAt.current)),
    );
    return () => clearTimeout(timer);
  }, [beat, isAuthenticated, pathname, visible]);
}
